import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useApp } from './lib/AppContext';
import { ScopeProvider } from './lib/scope';
import { StepUpHost } from './components/StepUp';
import { LeafTermSync } from './components/LeafTermSync';
import { LoginPage } from './features/auth/LoginPage';
import { ActivatePage } from './features/auth/ActivatePage';
import { SsoDonePage, SsoStartPage, SsoFirstFactorPage, SsoStepUpPage } from './features/auth/SsoPages';
import { Shell } from './features/shell/Shell';
import { OverviewPage } from './features/overview/OverviewPage';
import { DevicesPage } from './features/devices/DevicesPage';
import { DeviceDetailPage } from './features/devices/DeviceDetailPage';
import { NotFound } from './features/shell/NotFound';
import { TerminalPage } from './features/devices/TerminalPage';
import { EventDetailPage } from './features/events/EventDetailPage';
import { LivePage } from './features/live/LivePage';
import { EventsPage } from './features/events/EventsPage';
import { PlaybackPage } from './features/playback/PlaybackPage';
import { FirmwarePage } from './features/firmware/FirmwarePage';
import { MomentPage } from './features/playback/MomentPage';
import { SyncPlaybackPage } from './features/playback/SyncPlaybackPage';
import { RulesPage } from './features/rules/RulesPage';
import { MapPage } from './features/map/MapPage';
import { PeoplePage } from './features/people/PeoplePage';
import { AuditPage } from './features/audit/AuditPage';
import { SettingsPage } from './features/settings/SettingsPage';
import { SupportPage } from './features/support/SupportPage';
import { MfaAlarmNotice } from './features/notifications/MfaAlarmNotice';
import { AiPage } from './features/ai/AiPage';
import { BackupPage } from './features/backup/BackupPage';
import { ExportsPage, ExportsVerifyPage } from './features/exports/ExportsPage';
import { SharesPage } from './features/exports/SharesPage';
import { SharePage } from './features/exports/SharePage';
import { VerifyPage } from './features/exports/VerifyPage';
import { BillingPage } from './features/billing/BillingPage';
import { InventoryPage } from './features/inventory/InventoryPage';
import { DevicesLayout } from './features/devices/DevicesLayout';
import { SitesPage } from './features/orgtree/SitesPage';
import { OrgPage } from './features/settings/OrgSection';
import { SiteRoomPage } from './features/siteroom/SiteRoomPage';
import { useMultiScreenSync } from './lib/useMultiScreen';
import { IncidentsPage } from './features/kmgr/IncidentsPage';
import { IncidentPage } from './features/kmgr/IncidentPage';
import { HomeSettingsPage } from './features/kmgr/HomeSettingsPage';
import { OrgKmgrPage } from './features/kmgr/OrgKmgrPage';
import { HostsPage } from './features/kmgr/HostsPage';
import { NetworkPage } from './features/kmgr/NetworkPage';
import { RemotePage } from './features/kmgr/remote/RemotePage';
import { MyCamAddPage } from './features/mycam/MyCamAddPage';
import { isSimple } from './features/shell/mycamMode';
import { EnergyPage } from './features/energy/EnergyPage';
import { OrgEnergyPage } from './features/energy/OrgTariffs';
import { LeafEnergySettingsPage } from './features/energy/LeafSettings';

export function App() {
  const { session, config, uiMode } = useApp();
  const loc = useLocation();
  useMultiScreenSync(); // LIVE-10：視窗之間同步登入登出與操作活動
  // 免登入的公開頁：分享播放（EXP-05）、證據包驗證（EXP-04）
  if (loc.pathname.startsWith('/share/'))
    return (
      <Routes>
        <Route path="/share/:token" element={<SharePage />} />
      </Routes>
    );
  // 證據包驗證：免登入的 /verify 給外部收件人（沒有殼層）；登入的人到殼層內的「匯出與證據 › 驗證證據包」（/exports/verify，B-20）
  if (loc.pathname === '/verify' && !session) return <VerifyPage />;
  // 企業 SSO（plan SPEC #vms-web-sso-ui）：IdP 轉回的完成頁不論有沒有登入都先處理；入口圖示的 /sso/start 沒登入也能開
  if (loc.pathname === '/sso/done') return <SsoDonePage />;
  if (loc.pathname === '/sso/start' && !session) return <SsoStartPage />;
  // 接受邀請：/activate，或邀請連結原樣轉過來的 /i?env=…#t=…
  if (!session) return loc.pathname === '/activate' || loc.pathname === '/i' ? <ActivatePage /> : <LoginPage />;
  return (
    <ScopeProvider>
      <LeafTermSync />
      <StepUpHost />
      <Routes>
        {/* Kinio Node 終端機新視窗：沒有側欄（SPEC #node-ttl-vms-terminal） */}
        <Route path="terminal/:homeId/:aid" element={<TerminalPage />} />
        <Route element={<Shell />}>
          <Route index element={isSimple(config, uiMode) ? <Navigate to="/live" replace /> : <OverviewPage />} />
          <Route path="mycam/add" element={<MyCamAddPage />} />
          {/* 設備頁分頁：全部設備｜韌體｜庫存與開通（SPEC #vms-web-220；舊 /inventory 在殼層轉址） */}
          <Route path="devices" element={<DevicesLayout />}>
            <Route index element={<DevicesPage />} />
            <Route path="firmware" element={<FirmwarePage />} />
            <Route path="inventory/*" element={<InventoryPage />} />
          </Route>
          <Route path="devices/:homeId/:aid" element={<DeviceDetailPage />} />
          <Route path="sites" element={<SitesPage />} />
          {/* 能源管理（plan SPEC #energy-ui-vms）：設備與場域 › 用電；組織設定 › 電價；〈{leaf}〉› 用電設定 */}
          <Route path="energy/*" element={<EnergyPage />} />
          <Route path="org/energy" element={<OrgEnergyPage />} />
          <Route path="sites/energy/:homeId" element={<LeafEnergySettingsPage />} />
          <Route path="org/*" element={<OrgPage />} />
          {/* Kinio KMS 操作台（SPEC #kinio-kms-ui）：IT 監控 › 事故；組織設定 › IT 監控；組織與場域 › 〈家戶〉› 監控設定 */}
          <Route path="kmgr" element={<Navigate to="/kmgr/incidents" replace />} />
          <Route path="kmgr/incidents" element={<IncidentsPage />} />
          <Route path="kmgr/incidents/:homeId/:incidentId" element={<IncidentPage />} />
          <Route path="kmgr/hosts" element={<HostsPage />} />
          <Route path="kmgr/hosts/:homeId/:targetRef" element={<HostsPage />} />
          <Route path="kmgr/network" element={<NetworkPage />} />
          <Route path="kmgr/remote" element={<RemotePage />} />
          <Route path="org/kmgr/*" element={<OrgKmgrPage />} />
          <Route path="sites/kmgr/:homeId/*" element={<HomeSettingsPage />} />
          <Route path="activate" element={<ActivatePage />} />
          <Route path="i" element={<ActivatePage />} />
          <Route path="live" element={<LivePage />} />
          <Route path="events" element={<EventsPage />} />
          <Route path="events/:homeId/:eventId" element={<EventDetailPage />} />
          <Route path="playback" element={<PlaybackPage />} />
          <Route path="playback/sync" element={<SyncPlaybackPage />} />
          <Route path="playback/moment" element={<MomentPage />} />
          <Route path="rules" element={<RulesPage />} />
          <Route path="map" element={<MapPage />} />
          <Route path="ai" element={<AiPage />} />
          <Route path="backup" element={<BackupPage />} />
          <Route path="exports" element={<ExportsPage />} />
          <Route path="exports/shares" element={<SharesPage />} />
          <Route path="exports/verify" element={<ExportsVerifyPage />} />
          <Route path="people/*" element={<PeoplePage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="settings/*" element={<SettingsPage />} />
          <Route path="support/*" element={<SupportPage />} />
          <Route path="alerts/mfa" element={<MfaAlarmNotice />} />
          <Route path="sso/step-up" element={<SsoStepUpPage />} />
          <Route path="sso/first-factor" element={<SsoFirstFactorPage />} />
          <Route path="sso/start" element={<Navigate to="/" replace />} />
          {/* Kinio Station 工位頁已搬到 develop portal（開發控制只收開發者帳號，platform#98，Eddy 2026-10-22 選單）；/workbench 落到 404 */}
          <Route path="siteroom/*" element={<SiteRoomPage />} />
          {/* 未知網址：404（有明顯對應的直接轉址），不顯示總覽冒充（第三輪 B3-18） */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </ScopeProvider>
  );
}
